import Box from "@mui/material/Box";

import { useDispatch, useSelector } from "react-redux";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from "@mui/material/Tooltip";
import LinearProgress from "@mui/material/LinearProgress";
import { actionDataSearchRooms } from "../Data";

const DELIMITER = ";";

const root_ = (theme) => ({
  "& .MuiTypography-overline": {
    lineHeight: 1.25,
    letterSpacing: "0.03em",
  },
});

const table_ = (theme) => ({
  marginBottom: "16px",
  lineHeight: 1.1,
});

const actionIcon_ = (theme) => ({
  padding: "2px",
  marginRight: "2px",
  marginTop: "-3px",
  fontSize: "1rem",
  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
  },
});

const flex_ = (theme) => ({
  display: "flex",
});

const alignCenter_ = (theme) => ({
  alignItems: "center",
});

const flexBetween_ = (theme) => ({
  justifyContent: "space-between",
});

const flexStart_ = (theme) => ({
  justifyContent: "flex-start",
});

const flexEnd_ = (theme) => ({
  justifyContent: "flex-end",
});

const mb2_ = (theme) => ({
  marginBottom: "2px",
});

const mr4_ = (theme) => ({
  marginRight: "4px",
});

export default function AdditionalInfo({ room }) {
  const dispatch = useDispatch();
  const people = useSelector((state) => state.infoReducer.roomPeople);
  const peopleLoading = useSelector(
    (state) => state.infoReducer.roomPeopleLoading,
  );

  const {
    bldrecnbr,
    bld_descrshort,
    falist,
    grantdtlist,
    deptid,
    dept_descr,
    occdeptlist,
    rmfnclist,
  } = room;

  const Occdeptlist = ListSplitter(occdeptlist, DELIMITER);
  const Rmfnclist = ListSplitter(rmfnclist, DELIMITER);
  const Falist = FaList(falist, bldrecnbr, bld_descrshort, DELIMITER);
  const Grantdtlist = ListSplitter(grantdtlist, DELIMITER);

  const searchDept = () => {
    const filters = [
      { type: "building", bldrecnbr, label: bld_descrshort },
      { type: "department", deptid, label: dept_descr },
    ];
    dispatch(actionDataSearchRooms(filters));
  };

  const displayPeople = people
    .filter((person) => {
      return !person.person_uniqname.startsWith("*"); // filter out *na
    })
    .sort((a, b) => {
      const aIsOcc = a.rmoccupant === "Y";
      const bIsOcc = b.rmoccupant === "Y";
      if (aIsOcc !== bIsOcc) {
        return aIsOcc ? -1 : 1;
      }
      const aString = `${a.person_name}${a.uniqname}`;
      const bString = `${b.person_name}${b.uniqname}`;
      return aString > bString ? 1 : -1;
    });

  return (
    <Box sx={root_}>
      <Grid container spacing={0} sx={table_}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Survey Dept
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="overline">
            {dept_descr} ({deptid})
          </Typography>
          <Tooltip
            title={`Search ${dept_descr.toUpperCase()} in ${bld_descrshort}`}
          >
            <IconButton
              color="primary"
              onClick={searchDept}
              sx={actionIcon_}
              size="large"
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid container spacing={0} sx={table_}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Occupying Dept
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>{Occdeptlist}</Grid>
      </Grid>
      <Grid container spacing={0} sx={table_}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Room Function
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>{Rmfnclist}</Grid>
      </Grid>
      <Grid container spacing={0} sx={table_}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            People
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <PeopleList people={displayPeople} loading={peopleLoading} />
        </Grid>
      </Grid>
      <Grid container spacing={0} sx={table_}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Faculty Allocation
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>{Falist}</Grid>
      </Grid>
      <Grid container spacing={0} sx={table_}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Grant
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>{Grantdtlist}</Grid>
      </Grid>
    </Box>
  );
}

function PeopleList({ people, loading }) {
  return (
    <>
      {loading ? <LinearProgress /> : ""}
      {people.map((person) => {
        const {
          person_name,
          person_uniqname,
          rmrole_descr,
          rmrole,
          rmoccupant,
        } = person;
        const link = `https://mcommunity.umich.edu/person/${person_uniqname}`;
        const isOccupant = rmoccupant === "Y";
        return (
          <Box key={person_uniqname} sx={[flex_, flexBetween_, mb2_]}>
            <Box sx={[flex_, flexStart_, alignCenter_]}>
              <Typography sx={mr4_} variant="overline" display="block">
                {person_name}
              </Typography>
              <Tooltip
                describeChild
                placement="top"
                title="Open M-Community Profile in a new window"
              >
                <Typography sx={mr4_} variant="caption">
                  (
                  <a href={link} target="_blank" rel="noopener noreferrer">
                    {person_uniqname}
                  </a>
                  )
                </Typography>
              </Tooltip>
              <Tooltip describeChild placement="top" title={rmrole_descr}>
                <Typography sx={mr4_} variant="overline">
                  {rmrole}
                </Typography>
              </Tooltip>
            </Box>
            {isOccupant && (
              <Box sx={[flex_, flexEnd_, alignCenter_]}>
                <Tooltip
                  describeChild
                  placement="right"
                  title="Occupy Checkbox checked in MPathways Data"
                >
                  <Typography variant="overline">Occupant</Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
}

function FaList(list = "", bldrecnbr, bld_descrshort, seperator) {
  const dispatch = useDispatch();

  const occupants =
    !list || !list.split
      ? []
      : list.split(seperator).map((occ) => {
          let tempOcc = occ ? occ.trim() : "";
          let temp = tempOcc ? tempOcc.split("(") : [""];
          let uniqname = temp[0].toLowerCase();

          return {
            uniqname,
            label: tempOcc,
            link: `https://mcommunity.umich.edu/person/${uniqname}`,
            search: () => {
              const filters = [
                { type: "building", bldrecnbr, label: bld_descrshort },
                { type: "person", person_uniqname: uniqname, label: uniqname },
              ];
              dispatch(actionDataSearchRooms(filters));
            },
          };
        });

  return (
    <Box>
      {list &&
        occupants &&
        occupants.length &&
        occupants.map((occ) => {
          return (
            <Box key={occ.label}>
              <Typography variant="overline">{occ.label}</Typography>
              <Tooltip
                title={`Search ${occ.uniqname.toUpperCase()} in ${bld_descrshort}`}
              >
                <IconButton
                  color="primary"
                  onClick={occ.search}
                  sx={actionIcon_}
                  size="large"
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
    </Box>
  );
}

function ListSplitter(list, seperator) {
  return list && list.length && list.split ? (
    <Box>
      {list.split(seperator).map((item) => (
        <Box key={item}>
          <Typography variant="overline">{item}</Typography>
        </Box>
      ))}
    </Box>
  ) : (
    ""
  );
}
